import * as React from 'react';
import { memo, useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import AsyncSelect from 'react-select/async';
import { useApolloClient } from '@apollo/client';
import moment from 'moment';

import { popupData, PopupNames } from 'core';
import {
  usePopupStateOperations,
  useExpense,
  useExpenseCategories,
  useExpenseMutations,
} from 'core/hooks';
import {
  ExpenseSource,
  ExpenseCurrency,
  ExpenseRecurrence,
  ExpenseStatus,
  useReceivedInvoicesForExpenseQuery,
  PaginatedGroupsDocument,
  PaginatedGroupsQuery,
  PaginatedGroupsQueryVariables,
} from 'core/graphql';
import { formatMoneyCurrency } from 'core/utils/format';
import { Button, Input, InputLabel, DateSelect } from 'atoms';
import { Colors } from 'services/ColorService';

import { PopupContainer } from '../PopupContainer';

export interface IExpenseFormPopupData {
  /** Edit when `expenseId` is set, create otherwise. */
  expenseId?: string;
  /** Seed values on create — e.g. the calendar day the user clicked. */
  prefill?: {
    date?: string;
  };
  onSaved?: () => void;
}

interface ISelectOpt {
  value: string;
  label: string;
}

const CURRENCY_OPTIONS: ISelectOpt[] = Object.values(
  ExpenseCurrency,
).map((c) => ({ value: c, label: c }));

const RECURRENCE_VALUES: ExpenseRecurrence[] = [
  ExpenseRecurrence.None,
  ExpenseRecurrence.Weekly,
  ExpenseRecurrence.Monthly,
];

const STATUS_VALUES: ExpenseStatus[] = [
  ExpenseStatus.Planned,
  ExpenseStatus.Confirmed,
  ExpenseStatus.Paid,
  ExpenseStatus.Cancelled,
];

// i18n keys are lowercase (`recurrence.weekly`, `statuses.paid`); the enum
// values arrive UPPER-CASE off codegen — map once.
const recurrenceKey = (r: ExpenseRecurrence): string => r.toLowerCase();
const statusKey = (s: ExpenseStatus): string => s.toLowerCase();

interface IFormState {
  date: string | null;
  paidAt: string | null;
  amount: string;
  currency: string;
  categorySlug: string;
  description: string;
  counterpartyName: string;
  counterpartyTaxNo: string;
  documentUrl: string;
  notes: string;
  relatedGroupId: string;
  recurrence: ExpenseRecurrence;
  recurrenceDay: string;
  recurrenceUntil: string | null;
  status: ExpenseStatus;
  relatedFakturowniaInvoiceId: string;
  /** Manual credit-note / correction — when on, a non-positive (negative or
      zero) amount is allowed and a reason in `notes` is required. */
  isCorrection: boolean;
}

const emptyForm: IFormState = {
  date: null,
  paidAt: null,
  amount: '',
  // Phase 4: PLN is LearnIT's operating currency — always default new
  // expenses to it, regardless of `REACT_APP_LANG`. Edit mode hydrates from
  // the existing expense, so historical BYN/USD rows still render correctly.
  currency: ExpenseCurrency.Pln,
  categorySlug: '',
  description: '',
  counterpartyName: '',
  counterpartyTaxNo: '',
  documentUrl: '',
  notes: '',
  relatedGroupId: '',
  recurrence: ExpenseRecurrence.None,
  recurrenceDay: '',
  recurrenceUntil: null,
  status: ExpenseStatus.Planned,
  relatedFakturowniaInvoiceId: '',
  isCorrection: false,
};

const isValidUrl = (value: string): boolean =>
  !value || /^https:\/\/\S+$/i.test(value);

// Pull-down window for the received-invoice picker — wide enough to cover the
// typical "unpaid cost docs" backlog without unbounded fetches.
const INVOICE_PICKER_FROM = moment().subtract(1, 'year').format('YYYY-MM-DD');
const INVOICE_PICKER_TO = moment().add(3, 'months').format('YYYY-MM-DD');

/**
 * Single create+edit modal for `Expense`. Money fields lock for two reasons:
 * the row is Fakturownia-sourced (`source==='fakturownia'`), OR the user just
 * linked a received invoice in this form — both make the invoice the source of
 * truth for amount/currency/date/counterparty (TZ §3.7, unified-expense plan).
 */
export const ExpenseFormPopup = memo(() => {
  const { t } = useTranslation();
  const {
    operations: { hidePopup },
  } = usePopupStateOperations();

  const data = popupData()[PopupNames.expenseForm];
  const isEdit = Boolean(data?.expenseId);

  const { expense } = useExpense(data?.expenseId);
  const { categories } = useExpenseCategories();
  const {
    createExpense,
    updateExpense,
    deleteExpense,
    loading,
  } = useExpenseMutations();

  const [form, setForm] = useState<IFormState>(emptyForm);
  const [errors, setErrors] = useState<
    Partial<Record<keyof IFormState, string>>
  >({});
  // Preserve the group's display label across edits — the lazy group query
  // only returns rows the user has searched for, so we cache the selected
  // option locally to keep it visible.
  const [groupOption, setGroupOption] = useState<ISelectOpt | null>(null);
  // Cache the linked invoice's option for display — the picker query runs with
  // `unlinkedOnly`, so an already-linked invoice is excluded from its options;
  // without this cache the picker renders blank when editing a linked expense.
  const [invoiceOption, setInvoiceOption] = useState<ISelectOpt | null>(null);
  // True right after linking a *paid* invoice auto-flipped the status to Paid —
  // drives the "status taken from the invoice" hint. Cleared on manual edit.
  const [statusFromInvoice, setStatusFromInvoice] = useState(false);

  // Received-invoice picker options (unlinked received cost docs).
  const { data: invoicesData } = useReceivedInvoicesForExpenseQuery({
    variables: {
      dateFrom: INVOICE_PICKER_FROM,
      dateTo: INVOICE_PICKER_TO,
      unlinkedOnly: true,
    },
    fetchPolicy: 'cache-and-network',
  });

  const apollo = useApolloClient();

  // Hydrate the form once we have the expense — the popup mounts before the
  // query resolves, so we listen for `expense` to flip.
  useEffect(() => {
    if (!isEdit) {
      setForm({
        ...emptyForm,
        date: data?.prefill?.date || null,
      });
      setGroupOption(null);
      setInvoiceOption(null);
      setStatusFromInvoice(false);
      return;
    }
    if (!expense) return;
    setForm({
      date: expense.date || null,
      paidAt: expense.paidAt || null,
      amount: expense.amount != null ? String(expense.amount) : '',
      currency: expense.currency || ExpenseCurrency.Pln,
      categorySlug: expense.category?.slug || '',
      description: expense.description || '',
      counterpartyName: expense.counterpartyName || '',
      counterpartyTaxNo: expense.counterpartyTaxNo || '',
      documentUrl: expense.documentUrl || '',
      notes: expense.notes || '',
      relatedGroupId: expense.relatedGroup?.id || '',
      recurrence: expense.recurrence || ExpenseRecurrence.None,
      recurrenceDay:
        expense.recurrenceDay != null ? String(expense.recurrenceDay) : '',
      recurrenceUntil: expense.recurrenceUntil || null,
      status: expense.status || ExpenseStatus.Planned,
      // Key by pk to match `invoiceOptions` (which use String(pk)).
      relatedFakturowniaInvoiceId: expense.relatedFakturowniaInvoice
        ? String(expense.relatedFakturowniaInvoice.pk)
        : '',
      // A Fakturownia-synced correction, or a stored manual negative entry.
      isCorrection:
        Boolean(expense.isCorrection) ||
        (expense.source !== ExpenseSource.Fakturownia &&
          (expense.amount ?? 0) < 0),
    });
    setGroupOption(
      expense.relatedGroup
        ? { value: expense.relatedGroup.id, label: expense.relatedGroup.name }
        : null,
    );
    setInvoiceOption(
      expense.relatedFakturowniaInvoice
        ? {
            value: String(expense.relatedFakturowniaInvoice.pk),
            label:
              expense.relatedFakturowniaInvoice.number ||
              `#${expense.relatedFakturowniaInvoice.pk}`,
          }
        : null,
    );
  }, [expense, isEdit, data]);

  const invoiceOptions = useMemo<ISelectOpt[]>(
    () =>
      (invoicesData?.receivedInvoicesForExpense || [])
        .filter((inv): inv is NonNullable<typeof inv> => Boolean(inv))
        .map((inv) => ({
          value: String(inv.pk),
          label: t('pages.fakturownia.expenseForm.invoicePicker.option', {
            number: inv.number || '—',
            buyerName: inv.buyerName || '—',
            priceGross: formatMoneyCurrency(inv.priceGross, inv.currency),
            currency: inv.currency || '',
          }),
        })),
    [invoicesData, t],
  );

  // Money lock: existing Fakturownia rows OR a freshly linked invoice.
  const isFakturownia = expense?.source === ExpenseSource.Fakturownia;
  const hasInvoiceLink = Boolean(form.relatedFakturowniaInvoiceId);
  const moneyLocked = isFakturownia || hasInvoiceLink;

  const categoryOptions = useMemo<ISelectOpt[]>(
    () => categories.map((c) => ({ value: c.slug, label: c.name })),
    [categories],
  );

  const loadGroupOptions = useCallback(
    async (inputValue: string): Promise<ISelectOpt[]> => {
      const result = await apollo.query<
        PaginatedGroupsQuery,
        PaginatedGroupsQueryVariables
      >({
        query: PaginatedGroupsDocument,
        variables: { query: inputValue || undefined, first: 20 },
        fetchPolicy: 'no-cache',
      });
      const edges = result.data?.allGroups?.edges || [];
      return edges
        .map((edge) => edge?.node)
        .filter((node): node is NonNullable<typeof node> => Boolean(node))
        .map((node) => ({ value: node.id, label: node.name }));
    },
    [apollo],
  );

  const close = useCallback(() => {
    setForm(emptyForm);
    setErrors({});
    setGroupOption(null);
    setInvoiceOption(null);
    setStatusFromInvoice(false);
    hidePopup(PopupNames.expenseForm);
  }, [hidePopup]);

  const setField = useCallback(
    <K extends keyof IFormState>(key: K, value: IFormState[K]) => {
      setForm((prev) => ({ ...prev, [key]: value }));
    },
    [],
  );

  // Linking an invoice prefills + locks money fields FROM the invoice — mirror
  // of the backend `_apply_invoice_lock`. Clearing it unlocks them again.
  const onInvoiceChange = useCallback(
    (opt: ISelectOpt | null) => {
      if (!opt) {
        setInvoiceOption(null);
        setStatusFromInvoice(false);
        setForm((prev) => ({ ...prev, relatedFakturowniaInvoiceId: '' }));
        return;
      }
      setInvoiceOption(opt);
      const inv = (invoicesData?.receivedInvoicesForExpense || []).find(
        (i) => i && String(i.pk) === opt.value,
      );
      // A paid invoice → a paid expense. Preview it in the status select; the
      // backend `_apply_invoice_lock` enforces the same on save. Still
      // overridable via the manual status select.
      const invoicePaid = inv?.status === 'paid';
      setStatusFromInvoice(invoicePaid);
      setForm((prev) => ({
        ...prev,
        relatedFakturowniaInvoiceId: opt.value,
        amount: inv?.priceGross != null ? String(inv.priceGross) : prev.amount,
        currency: inv?.currency || prev.currency,
        date: inv?.issueDate || prev.date,
        counterpartyName: inv?.buyerName || prev.counterpartyName,
        counterpartyTaxNo: inv?.buyerTaxNo || prev.counterpartyTaxNo,
        status: invoicePaid ? ExpenseStatus.Paid : prev.status,
      }));
    },
    [invoicesData],
  );

  const validate = useCallback((): boolean => {
    const next: typeof errors = {};
    if (!form.date)
      next.date = t('pages.fakturownia.expenseForm.errors.dateRequired');
    const num = parseFloat(form.amount);
    if (!form.amount) {
      next.amount = t('pages.fakturownia.expenseForm.errors.amountRequired');
    } else if (Number.isNaN(num)) {
      next.amount = t('pages.fakturownia.expenseForm.errors.amountPositive');
    } else if (!form.isCorrection && num <= 0) {
      // A correction/credit-note may be negative or zero (metadata-only); a
      // normal expense must be positive. The signed amount is accepted by the
      // backend (Expense.amount has no positivity guard) and nets out in
      // aggregates/the calendar.
      next.amount = t('pages.fakturownia.expenseForm.errors.amountPositive');
    }
    if (form.isCorrection && !form.notes.trim()) {
      next.notes = t('pages.fakturownia.expenseForm.errors.correctionReason');
    }
    if (!form.currency)
      next.currency = t(
        'pages.fakturownia.expenseForm.errors.currencyRequired',
      );
    if (form.documentUrl && !isValidUrl(form.documentUrl)) {
      next.documentUrl = t('pages.fakturownia.expenseForm.errors.urlInvalid');
    }
    setErrors(next);
    return Object.keys(next).length === 0;
  }, [form, t]);

  const onSubmit = useCallback(async () => {
    if (!validate()) return;
    // A correction / credit-note must NET OUT the original, so its stored
    // amount is non-positive. Coerce the sign here instead of trusting the
    // operator to type a minus — a positive "correction" would otherwise be
    // summed as an ordinary expense and inflate every total. (Fakturownia rows
    // are money-locked server-side, so this only affects manual entries.)
    const raw = parseFloat(form.amount);
    const amount = form.isCorrection ? -Math.abs(raw) : raw;
    const recurrenceDay =
      form.recurrence === ExpenseRecurrence.Monthly && form.recurrenceDay
        ? parseInt(form.recurrenceDay, 10)
        : undefined;
    const common = {
      date: form.date as string,
      currency: form.currency,
      categorySlug: form.categorySlug || undefined,
      description: form.description || undefined,
      counterpartyName: form.counterpartyName || undefined,
      counterpartyTaxNo: form.counterpartyTaxNo || undefined,
      documentUrl: form.documentUrl || undefined,
      paidAt: form.paidAt || undefined,
      notes: form.notes || undefined,
      relatedGroupId: form.relatedGroupId || undefined,
      // Codegen enum values are UPPER-CASE ('NONE'/'PLANNED'…) but the backend
      // validates the RAW lowercase string — lowercase before sending.
      recurrence: recurrenceKey(form.recurrence),
      recurrenceDay,
      recurrenceUntil:
        form.recurrence !== ExpenseRecurrence.None
          ? form.recurrenceUntil || undefined
          : undefined,
      status: statusKey(form.status),
      relatedFakturowniaInvoiceId:
        form.relatedFakturowniaInvoiceId || undefined,
    };

    let ok = false;
    if (isEdit && data?.expenseId) {
      ok = await updateExpense({
        id: data.expenseId,
        ...common,
        amount,
      });
    } else {
      ok = await createExpense({
        ...common,
        amount,
      });
    }
    if (ok) {
      data?.onSaved?.();
      close();
    }
  }, [validate, form, isEdit, data, updateExpense, createExpense, close]);

  const onDelete = useCallback(async () => {
    if (!data?.expenseId) return;
    const ok = await deleteExpense(data.expenseId);
    if (ok) {
      data.onSaved?.();
      close();
    }
  }, [data, deleteExpense, close]);

  if (!data) return null;

  const title = isEdit
    ? t('pages.fakturownia.expenseForm.titleEdit', {
        pk: expense?.pk || data.expenseId,
      })
    : t('pages.fakturownia.expenseForm.titleCreate');

  const selectedInvoiceOption =
    invoiceOption ||
    invoiceOptions.find((o) => o.value === form.relatedFakturowniaInvoiceId) ||
    null;

  return (
    <PopupContainer width="720px" title={title} onClose={close}>
      <FormGrid>
        <Field>
          <InputLabel hasError={Boolean(errors.date)}>
            {t('pages.fakturownia.expenseForm.fields.date')}
          </InputLabel>
          <DateSelect
            value={form.date ? moment(form.date).toDate() : null}
            onChange={(d) =>
              setField('date', d ? moment(d).format('YYYY-MM-DD') : null)
            }
            placeholder="YYYY-MM-DD"
            hasError={Boolean(errors.date)}
            disabled={moneyLocked}
          />
          {moneyLocked ? (
            <LockedHint>
              {t('pages.fakturownia.expenseForm.lockedHint')}
            </LockedHint>
          ) : null}
        </Field>

        <Field>
          <InputLabel>
            {t('pages.fakturownia.expenseForm.fields.paidAt')}
          </InputLabel>
          <DateSelect
            value={form.paidAt ? moment(form.paidAt).toDate() : null}
            onChange={(d) =>
              setField('paidAt', d ? moment(d).format('YYYY-MM-DD') : null)
            }
            placeholder="YYYY-MM-DD"
          />
          <LockedHint>
            {t('pages.fakturownia.expenseForm.paidAtHint')}
          </LockedHint>
        </Field>

        <Field>
          <InputLabel hasError={Boolean(errors.amount)}>
            {t('pages.fakturownia.expenseForm.fields.amount')}
          </InputLabel>
          <Input
            type="number"
            value={form.amount}
            onChange={({ target: { value } }) => setField('amount', value)}
            hasError={Boolean(errors.amount)}
            disabled={moneyLocked}
            financialNumber
          />
        </Field>

        <Field>
          <label
            htmlFor="expense-correction-cb"
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 6,
              fontSize: 13,
            }}
          >
            <input
              id="expense-correction-cb"
              type="checkbox"
              checked={form.isCorrection}
              onChange={(e) => setField('isCorrection', e.target.checked)}
              disabled={moneyLocked}
            />
            {t('pages.fakturownia.expenseForm.fields.isCorrection')}
          </label>
          {form.isCorrection ? (
            <div style={{ fontSize: 11, color: '#64748b', marginTop: 4 }}>
              {t('pages.fakturownia.expenseForm.correctionHint')}
            </div>
          ) : null}
        </Field>

        <Field>
          <InputLabel hasError={Boolean(errors.currency)}>
            {t('pages.fakturownia.expenseForm.fields.currency')}
          </InputLabel>
          <SelectNative
            value={form.currency}
            onChange={(e) => setField('currency', e.target.value)}
            disabled={moneyLocked}
          >
            {CURRENCY_OPTIONS.map((opt) => (
              <option key={opt.value} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </SelectNative>
        </Field>

        {expense?.amountPln != null ? (
          <Field>
            <InputLabel>
              {t('pages.fakturownia.expenseForm.fields.amountPln')}
            </InputLabel>
            <ReadonlyValue>
              {formatMoneyCurrency(expense.amountPln, ExpenseCurrency.Pln)}
            </ReadonlyValue>
          </Field>
        ) : null}

        <FieldFull>
          <InputLabel>
            {t('pages.fakturownia.expenseForm.invoicePicker.label')}
          </InputLabel>
          <AsyncSelect
            cacheOptions
            defaultOptions={invoiceOptions}
            value={selectedInvoiceOption}
            onChange={(v) => onInvoiceChange(v as ISelectOpt | null)}
            loadOptions={() => Promise.resolve(invoiceOptions)}
            placeholder={t(
              'pages.fakturownia.expenseForm.invoicePicker.placeholder',
            )}
            isClearable={!isFakturownia}
            isDisabled={isFakturownia}
          />
          {hasInvoiceLink ? (
            <LockedHint>
              {t('pages.fakturownia.expenseForm.invoicePicker.lockedHint')}
            </LockedHint>
          ) : null}
        </FieldFull>

        <FieldFull>
          <InputLabel>
            {t('pages.fakturownia.expenseForm.groupPicker.label')}
          </InputLabel>
          <AsyncSelect
            cacheOptions
            defaultOptions
            value={groupOption}
            onChange={(v) => {
              const opt = v as ISelectOpt | null;
              setGroupOption(opt);
              setField('relatedGroupId', opt?.value || '');
            }}
            loadOptions={loadGroupOptions}
            placeholder={t(
              'pages.fakturownia.expenseForm.groupPicker.placeholder',
            )}
            isClearable
          />
        </FieldFull>

        <Field>
          <InputLabel>
            {t('pages.fakturownia.expenseForm.fields.recurrence')}
          </InputLabel>
          <SelectNative
            value={form.recurrence}
            onChange={(e) =>
              setField('recurrence', e.target.value as ExpenseRecurrence)
            }
          >
            {RECURRENCE_VALUES.map((r) => (
              <option key={r} value={r}>
                {t(
                  `pages.fakturownia.expenseForm.recurrence.${recurrenceKey(
                    r,
                  )}`,
                )}
              </option>
            ))}
          </SelectNative>
        </Field>

        <Field>
          <InputLabel>
            {t('pages.fakturownia.expenseForm.fields.status')}
          </InputLabel>
          <SelectNative
            value={form.status}
            onChange={(e) => {
              setStatusFromInvoice(false);
              setField('status', e.target.value as ExpenseStatus);
            }}
          >
            {STATUS_VALUES.map((s) => (
              <option key={s} value={s}>
                {t(`pages.fakturownia.expenseForm.statuses.${statusKey(s)}`)}
              </option>
            ))}
          </SelectNative>
          {statusFromInvoice ? (
            <LockedHint>
              {t('pages.fakturownia.expenseForm.statusFromInvoice')}
            </LockedHint>
          ) : null}
        </Field>

        {form.recurrence === ExpenseRecurrence.Monthly ? (
          <Field>
            <InputLabel>
              {t('pages.fakturownia.expenseForm.fields.recurrenceDay')}
            </InputLabel>
            <Input
              type="number"
              value={form.recurrenceDay}
              onChange={({ target: { value } }) =>
                setField('recurrenceDay', value)
              }
            />
            <FieldHint>
              {t('pages.fakturownia.expenseForm.recurrenceDayHint')}
            </FieldHint>
          </Field>
        ) : null}

        {form.recurrence !== ExpenseRecurrence.None ? (
          <Field>
            <InputLabel>
              {t('pages.fakturownia.expenseForm.fields.recurrenceUntil')}
            </InputLabel>
            <DateSelect
              value={
                form.recurrenceUntil
                  ? moment(form.recurrenceUntil).toDate()
                  : null
              }
              onChange={(d) =>
                setField(
                  'recurrenceUntil',
                  d ? moment(d).format('YYYY-MM-DD') : null,
                )
              }
              placeholder="YYYY-MM-DD"
            />
            <FieldHint>
              {t('pages.fakturownia.expenseForm.recurrenceUntilHint')}
            </FieldHint>
          </Field>
        ) : null}

        <FieldFull>
          <InputLabel>
            {t('pages.fakturownia.expenseForm.fields.category')}
          </InputLabel>
          <AsyncSelect
            cacheOptions
            defaultOptions={categoryOptions}
            value={
              form.categorySlug
                ? categoryOptions.find((c) => c.value === form.categorySlug) ||
                  null
                : null
            }
            onChange={(v) =>
              setField('categorySlug', (v as ISelectOpt | null)?.value || '')
            }
            loadOptions={() => Promise.resolve(categoryOptions)}
            isClearable
          />
        </FieldFull>

        <FieldFull>
          <InputLabel>
            {t('pages.fakturownia.expenseForm.fields.description')}
          </InputLabel>
          <Input
            type="text"
            value={form.description}
            onChange={({ target: { value } }) => setField('description', value)}
          />
        </FieldFull>

        <Field>
          <InputLabel>
            {t('pages.fakturownia.expenseForm.fields.counterpartyName')}
          </InputLabel>
          <Input
            type="text"
            value={form.counterpartyName}
            onChange={({ target: { value } }) =>
              setField('counterpartyName', value)
            }
            disabled={moneyLocked}
          />
        </Field>

        <Field>
          <InputLabel>
            {t('pages.fakturownia.expenseForm.fields.counterpartyTaxNo')}
          </InputLabel>
          <Input
            type="text"
            value={form.counterpartyTaxNo}
            onChange={({ target: { value } }) =>
              setField('counterpartyTaxNo', value)
            }
            disabled={moneyLocked}
          />
        </Field>

        <FieldFull>
          <InputLabel hasError={Boolean(errors.documentUrl)}>
            {t('pages.fakturownia.expenseForm.fields.documentUrl')}
          </InputLabel>
          <Input
            type="text"
            value={form.documentUrl}
            onChange={({ target: { value } }) => setField('documentUrl', value)}
            hasError={Boolean(errors.documentUrl)}
          />
        </FieldFull>

        <FieldFull>
          <InputLabel>
            {t('pages.fakturownia.expenseForm.fields.notes')}
          </InputLabel>
          <TextArea
            value={form.notes}
            onChange={(e) => setField('notes', e.target.value)}
            rows={3}
          />
        </FieldFull>
      </FormGrid>

      <Actions>
        {isEdit && expense?.source === ExpenseSource.Manual ? (
          <Button
            onClick={onDelete}
            variant="outline"
            colors={{ backgroundColor: Colors.White, color: Colors.Red }}
            disabled={loading}
          >
            {t('pages.fakturownia.expenseForm.delete')}
          </Button>
        ) : null}
        <Spacer />
        <Button
          onClick={close}
          variant="outline"
          colors={{ backgroundColor: Colors.White, color: Colors.Blue }}
        >
          {t('pages.fakturownia.expenseForm.cancel')}
        </Button>
        <Button
          onClick={onSubmit}
          variant="primary"
          loading={loading}
          disabled={loading}
          colors={{ backgroundColor: Colors.Blue, color: Colors.White }}
        >
          {t('pages.fakturownia.expenseForm.save')}
        </Button>
      </Actions>
    </PopupContainer>
  );
});

const FormGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 16px;
  padding-top: 16px;

  @media (max-width: 720px) {
    grid-template-columns: 1fr;
  }
`;

const Field = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
`;

const FieldFull = styled(Field)`
  grid-column: 1 / -1;
`;

const LockedHint = styled.div`
  font-size: 11px;
  color: #92400e;
  background: #fffbeb;
  border: 1px solid #fde68a;
  padding: 6px 8px;
  border-radius: 6px;
`;

const FieldHint = styled.div`
  font-size: 11px;
  color: #6b7280;
`;

const ReadonlyValue = styled.div`
  height: 48px;
  display: flex;
  align-items: center;
  padding: 0 12px;
  background: #f3f4f6;
  border: 1px solid #e5e7eb;
  font-size: 14px;
  color: #374151;
`;

const SelectNative = styled.select`
  height: 48px;
  border: 1px solid #d1d5db;
  border-radius: 0;
  padding: 0 12px;
  background: #fff;
  font-size: 14px;
  outline: none;

  &:disabled {
    background: #f3f4f6;
    color: #9ca3af;
  }
`;

const TextArea = styled.textarea`
  border: 1px solid #d1d5db;
  border-radius: 0;
  padding: 8px 12px;
  font-size: 14px;
  font-family: inherit;
  outline: none;
  resize: vertical;

  &:focus {
    border-color: #2563eb;
  }
`;

const Actions = styled.div`
  display: flex;
  gap: 12px;
  margin-top: 24px;
  align-items: center;
`;

const Spacer = styled.div`
  flex: 1;
`;
